// Globals
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { createAdmin, getAdminDetails, updateAdminDetails } from '../../feature/actionCreators';
import { adminsSelector } from '../../feature/selectors';

// Components
import { IFormInstance, showSuccessToast } from '@/components';
import { Skeleton } from '@/ui';
import { CreateAdminForm } from '../../components';
import { StyledPageWrapper } from './CreateAdminContainerStyles';

// Helpers
import { TAdminFormValues } from '../../helpers/types';
import { AdminDetailsDTO } from '../../dtos';

interface ICreateAdminContainer {
  form: IFormInstance;
  id?: string | null;
  onFormSubmit: () => void;
}

const CreateAdminContainer = ({ form, id, onFormSubmit }: ICreateAdminContainer) => {
  const dispatch = useAppDispatch();
  const { isLoading } = useAppState(adminsSelector);
  const { t } = useTranslation(['admins']);
  const updateAction = id ? updateAdminDetails : createAdmin;

  const handleSubmit = async (values: TAdminFormValues) => {
    const adminDetailsDto = new AdminDetailsDTO(values, id);
    await dispatch(updateAction({ ...adminDetailsDto })).unwrap();
    onFormSubmit();
    showSuccessToast({ message: t('admin_data_saved') });
  };

  const getInitialData = useCallback(async (adminId?: string | null) => {
    if (adminId) {
      const adminData = await dispatch(getAdminDetails(adminId)).unwrap();
      form.setFieldsValue(adminData);
    }
  }, []);

  useEffect(() => {
    void getInitialData(id);
  }, [id]);

  if (isLoading) return <Skeleton />;

  return (
    <StyledPageWrapper>
      <CreateAdminForm handleSubmit={handleSubmit} isLoading={isLoading} form={form} id={id ?? null} />
    </StyledPageWrapper>
  );
};

export default CreateAdminContainer;
